import React, { useState, useEffect, useRef } from 'react';
import { 
  PaperAirplaneIcon, 
  UserIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { chatService } from '../../services/chatService';
import io from 'socket.io-client';

const Chat = ({ binId, onClose }) => {
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const messagesEndRef = useRef(null);
  const [typing, setTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);

  useEffect(() => {
    loadChat();
    
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (chat) {
      initializeSocket();
    }
  }, [chat]);

  useEffect(() => {
    if (chat) {
      setMessages(chat.messages);
    }
  }, [chat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChat = async () => {
    try {
      setLoading(true);
      const response = await chatService.getOrCreate(binId);
      setChat(response.data.data);
    } catch (error) {
      console.error('Error loading chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeSocket = () => {
    if (socket) {
      socket.disconnect();
    }

    const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:5000');
    
    newSocket.on('connect', () => {
      console.log('Connected to socket');
      if (chat) {
        newSocket.emit('join-chat', chat._id);
      }
    });

    newSocket.on('message-received', (data) => {
      if (data.chatId === chat?._id) {
        setMessages(prev => [...prev, data.message]);
      }
    });

    newSocket.on('user-typing', (data) => {
      if (data.chatId === chat?._id) {
        setOtherUserTyping(true);
        setTimeout(() => setOtherUserTyping(false), 3000);
      }
    });

    newSocket.on('user-stopped-typing', (data) => {
      if (data.chatId === chat?._id) {
        setOtherUserTyping(false);
      }
    });

    setSocket(newSocket);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !chat) return;

    try {
      const response = await chatService.sendMessage(chat._id, { message: newMessage.trim() });
      const sentMessage = response.data.data;
      
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');

      // Emit to socket
      if (socket) {
        socket.emit('new-message', {
          chatId: chat._id,
          message: sentMessage
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const typingTimeoutRef = useRef(null);

  const handleTyping = () => {
    if (socket && chat) {
      if (!typing) {
        setTyping(true);
        socket.emit('typing', { chatId: chat._id });
      }
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        setTyping(false);
        socket.emit('stop-typing', { chatId: chat._id });
      }, 1000);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getOtherParticipant = () => {
    if (!chat) return null;
    return chat.participants.find(p => p._id !== JSON.parse(localStorage.getItem('user'))?.id);
  };

  const isMyMessage = (message) => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    return message.sender._id === currentUser?.id;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="text-center text-gray-500 py-8">
        Chat not found
      </div>
    );
  }

  const otherParticipant = getOtherParticipant();

  return (
    <div className="flex flex-col h-96 bg-white rounded-lg shadow-xl border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-500 to-green-600 text-white">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
            <UserIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">
              {otherParticipant ? `${otherParticipant.firstName} ${otherParticipant.lastName}` : 'Chat'}
            </h3>
            <p className="text-xs text-green-100">
              Bin #{chat.bin._id.slice(-6)} • ₦{chat.bin.totalPrice.toLocaleString()}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-green-100 transition-colors"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Start a conversation about your materials</p>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message._id}
              className={`flex ${isMyMessage(message) ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-sm ${
                  isMyMessage(message)
                    ? 'bg-green-600 text-white rounded-br-md'
                    : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                }`}
              >
                <p className="text-sm leading-relaxed">{message.message}</p>
                <p
                  className={`text-xs mt-1 ${
                    isMyMessage(message) ? 'text-green-100' : 'text-gray-500'
                  }`}
                >
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
          ))
        )}
        
        {otherUserTyping && (
          <div className="flex justify-start">
            <div className="bg-white text-gray-900 px-4 py-3 rounded-2xl rounded-bl-md border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-1">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <span className="text-xs text-gray-500 ml-2">typing...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
            maxLength={500}
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim()}
            className="bg-green-600 text-white p-3 rounded-full hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Press Enter to send • Messages are cleared when sale is confirmed
        </p>
      </div>
    </div>
  );
};

export default Chat;
